/**
 * TrustGate ECS Adapters
 * 
 * Translate Firestore documents to canonical ECS types.
 * NO SCHEMA CHANGES - adapters only.
 * 
 * Pattern: Each adapter takes a raw Firestore doc and returns an ECS type.
 */

import type {
  TrustGateEvent,
  TrustGateEventType,
  TrustGateEpoch,
  TrustGateEntity,
  TrustGateEntityKind,
} from './types';
import type {
  TrustGateRequest,
  TrustGateProof,
  TrustGateProofMetadata,
  TrustGateEpoch as APIEpoch,
} from '../types';

/**
 * Adapter: TrustGateRequest → TrustGateEvent
 * Maps a request document to an event record
 */
export function adaptRequestToEvent(request: TrustGateRequest): TrustGateEvent {
  // Determine event type based on request status and decision
  let eventType: TrustGateEventType = 'request_created';
  
  if (request.status === 'APPROVED' || request.decision === 'ALLOW') {
    eventType = 'request_approved';
  } else if (request.status === 'DENIED' || request.decision === 'DENY') {
    eventType = 'request_denied';
  } else if (request.executionReceipt) {
    eventType = 'execution_completed';
  }

  return {
    id: request.id,
    type: eventType,
    timestamp: normalizeTimestamp(request.createdAt),
    entityId: request.id,
    entityKind: 'request',
    status: request.status,
    decision: request.decision as 'ALLOW' | 'DENY' | null,
    actorHash: hashUid(request.requestedBy),
    actorEmail: request.requestedByEmail || null,
    policyHash: null, // Not stored in v1 request schema
    proofId: request.proofId || null,
    lockedParams: request.lockedParams || null,
    reasonCode: request.reasonCode || null,
    sourceCollection: 'trustgate_requests',
  };
}

/**
 * Adapter: TrustGateProofMetadata (Firestore) → TrustGateEvent
 * Maps a proof Firestore document to an event record
 */
export function adaptProofMetadataToEvent(proof: TrustGateProofMetadata): TrustGateEvent {
  const eventType: TrustGateEventType = proof.revoked ? 'proof_revoked' : 'proof_issued';

  return {
    id: proof.proofId,
    type: eventType,
    timestamp: normalizeTimestamp(proof.createdAt),
    entityId: proof.proofId,
    entityKind: 'proof',
    status: proof.shareable ? 'SHAREABLE' : 'PRIVATE',
    decision: null, // Not stored in proof metadata
    actorHash: hashUid(proof.createdBy || null),
    actorEmail: null, // Not stored in proof docs
    policyHash: null, // Not stored in metadata
    proofId: proof.proofId,
    lockedParams: null, // Stored in linked request
    reasonCode: null,
    sourceCollection: 'trustgate_proofs',
  };
}

/**
 * Adapter: TrustGateProof (API) → TrustGateEvent
 * Maps an API proof response to an event record
 */
export function adaptProofAPIToEvent(proof: TrustGateProof): TrustGateEvent {
  return {
    id: proof.proofId,
    type: 'proof_issued',
    timestamp: normalizeTimestamp(proof.createdAt),
    entityId: proof.proofId,
    entityKind: 'proof',
    status: proof.status,
    decision: proof.decision as 'ALLOW' | 'DENY' | null,
    actorHash: hashUid(proof.decisionBy),
    actorEmail: null,
    policyHash: proof.policyVersion || null,
    proofId: proof.proofId,
    lockedParams: null,
    reasonCode: null,
    sourceCollection: 'api_response',
  };
}

/**
 * Adapter: systemEpoch API response → TrustGateEpoch
 * Maps epoch metadata to canonical ECS epoch
 */
export function adaptSystemEpochToECS(epoch: APIEpoch): TrustGateEpoch {
  return {
    epochId: epoch.epoch_id || null,
    planId: epoch.plan_id || null,
    activeHash: epoch.active_hash || null,
    policyHash: epoch.epoch_policy?.policy_packs?.[0]?.pack_id || null,
    gitSha: epoch.git_sha || null,
    deployedAt: epoch.deployed_at || null,
    source: epoch.source || 'unknown',
    policyPacks: (epoch.epoch_policy?.policy_packs || []).map(pack => ({
      id: pack.pack_id || 'unknown',
      version: pack.version || 'unknown',
    })),
    warnings: epoch.warnings || [],
    error: epoch.error || null,
  };
}

/**
 * Adapter: TrustGateRequest → TrustGateEntity
 * Maps a request to a generic entity record
 */
export function adaptRequestToEntity(request: TrustGateRequest): TrustGateEntity {
  return {
    id: request.id,
    kind: 'request',
    status: request.status,
    createdAt: normalizeTimestamp(request.createdAt),
    updatedAt: normalizeTimestamp(request.updatedAt),
    createdBy: hashUid(request.requestedBy),
    tenantId: request.tenantId || null,
    metadata: {
      actionType: request.actionType,
      lockedParams: request.lockedParams,
      decision: request.decision,
      decisionBy: request.decisionBy,
      decisionAt: request.decisionAt,
      proofId: request.proofId,
      executionReceipt: request.executionReceipt,
    },
  };
}

/**
 * Adapter: TrustGateProofMetadata (Firestore) → TrustGateEntity
 * Maps a proof Firestore document to a generic entity record
 */
export function adaptProofMetadataToEntity(proof: TrustGateProofMetadata): TrustGateEntity {
  return {
    id: proof.proofId,
    kind: 'proof',
    status: proof.shareable ? 'SHAREABLE' : 'PRIVATE',
    createdAt: normalizeTimestamp(proof.createdAt),
    updatedAt: null,
    createdBy: hashUid(proof.createdBy || null),
    tenantId: proof.tenantId || null,
    metadata: {
      requestId: proof.requestId,
      accessTier: proof.accessTier,
      expiresAt: proof.expiresAt,
      revoked: proof.revoked,
      shareable: proof.shareable,
      downloadCount: proof.downloadCount,
    },
  };
}

/**
 * Adapter: TrustGateProof (API) → TrustGateEntity
 * Maps an API proof response to a generic entity record
 */
export function adaptProofAPIToEntity(proof: TrustGateProof): TrustGateEntity {
  return {
    id: proof.proofId,
    kind: 'proof',
    status: proof.status,
    createdAt: normalizeTimestamp(proof.createdAt),
    updatedAt: null,
    createdBy: hashUid(proof.decisionBy),
    tenantId: null,
    metadata: {
      requestId: proof.requestId,
      decision: proof.decision,
      executionStatus: proof.executionStatus,
      policyVersion: proof.policyVersion,
      executionReceiptId: proof.executionReceiptId,
    },
  };
}

/**
 * Helper: Hash UID for privacy (stub - returns first 8 chars)
 * In production, use proper hashing (SHA256)
 */
function hashUid(uid: string | null): string | null {
  if (!uid) return null;
  // Stub: Return first 8 chars as "hash"
  // Production: Use crypto.subtle.digest('SHA-256', ...)
  return uid.slice(0, 8) + '...';
}

/**
 * Helper: Normalize timestamp to ISO 8601
 */
export function normalizeTimestamp(
  timestamp: string | { toDate(): Date; seconds: number } | null | undefined
): string {
  if (!timestamp) return new Date().toISOString();
  
  if (typeof timestamp === 'string') {
    return new Date(timestamp).toISOString();
  }
  
  if (typeof timestamp === 'object' && timestamp !== null) {
    if ('toDate' in timestamp && typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toISOString();
    }
    if ('seconds' in timestamp && typeof timestamp.seconds === 'number') {
      return new Date(timestamp.seconds * 1000).toISOString();
    }
  }
  
  return new Date().toISOString();
}

/**
 * Helper: Determine event type from request document
 */
export function deriveEventType(request: TrustGateRequest): TrustGateEventType {
  if (request.executionReceipt) {
    return 'execution_completed';
  }
  
  if (request.status === 'APPROVED' || request.decision === 'ALLOW') {
    return 'request_approved';
  }
  
  if (request.status === 'DENIED' || request.decision === 'DENY') {
    return 'request_denied';
  }
  
  return 'request_created';
}

/**
 * Helper: Sort events by timestamp (newest first)
 */
export function sortEventsByTimestamp(events: TrustGateEvent[]): TrustGateEvent[] {
  return events.sort((a, b) => {
    const timeA = new Date(a.timestamp).getTime();
    const timeB = new Date(b.timestamp).getTime();
    return timeB - timeA; // Descending order
  });
}

/**
 * Helper: Filter events by type
 */
export function filterEventsByType(
  events: TrustGateEvent[],
  types: TrustGateEventType[]
): TrustGateEvent[] {
  return events.filter(event => types.includes(event.type));
}

/**
 * Helper: Filter events by entity kind
 */
export function filterEventsByEntityKind(
  events: TrustGateEvent[],
  kinds: TrustGateEntityKind[]
): TrustGateEvent[] {
  return events.filter(event => kinds.includes(event.entityKind));
}

/**
 * Helper: Group events by date (YYYY-MM-DD)
 */
export function groupEventsByDate(
  events: TrustGateEvent[]
): Record<string, TrustGateEvent[]> {
  const grouped: Record<string, TrustGateEvent[]> = {};
  
  for (const event of events) {
    const date = event.timestamp.split('T')[0]; // Extract YYYY-MM-DD
    if (!grouped[date]) {
      grouped[date] = [];
    }
    grouped[date].push(event);
  }
  
  return grouped;
}
